import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';

export type TabStripSize = 'sm' | 'md' | 'lg';
export type TabStripVariant = 'default' | 'segmented' | 'underline';

export interface TabStripItem {
  id: string;
  label: string;
  disabled?: boolean;
  dot?: boolean;
  dotTitle?: string;
  dotColor?: string;
  dotRingColor?: string;
  dotBlinking?: boolean;
  dotBlinkPeriodMs?: number;
}

export interface TabStripClickEvent {
  id: string;
  item: TabStripItem;
  isActive: boolean;
}

@Component({
  selector: 'lib-tab-strip',
  standalone: true,
  template: `
    <div
      #tabStrip
      class="tab-strip"
      [class.tab-strip--size-sm]="size === 'sm'"
      [class.tab-strip--size-md]="size === 'md'"
      [class.tab-strip--size-lg]="size === 'lg'"
      [class.tab-strip--variant-default]="variant === 'default'"
      [class.tab-strip--variant-segmented]="variant === 'segmented'"
      [class.tab-strip--variant-underline]="variant === 'underline'"
      [class.tab-strip--stretch]="stretch"
      [class.tab-strip--wrap]="wrap"
      role="tablist"
      aria-orientation="horizontal"
      [attr.aria-label]="ariaLabel || null"
    >
      @for (item of items; track item.id) {
        <button
          #tabButton
          type="button"
          class="tab-strip__tab"
          [class.tab-strip__tab--active]="item.id === activeId"
          [class.tab-strip__tab--disabled]="item.disabled === true"
          [attr.aria-selected]="item.id === activeId"
          [attr.aria-controls]="item.id"
          role="tab"
          [disabled]="item.disabled === true"
          (click)="onSelect(item)"
        >
          <span class="tab-strip__tab-label">{{ item.label }}</span>
          @if (item.dot) {
            <span
              class="tab-strip__tab-dot"
              [class.tab-strip__tab-dot--blinking]="item.dotBlinking === true"
              [attr.title]="item.dotTitle || null"
              [attr.aria-label]="item.dotTitle || 'Indicator'"
              [style.background]="item.dotColor || null"
              [style.box-shadow]="item.dotRingColor ? '0 0 0 1px ' + item.dotRingColor : null"
              [style.animation-duration.ms]="item.dotBlinking ? (item.dotBlinkPeriodMs || 1200) : null"
            ></span>
          }
        </button>
      }
    </div>
  `,
  styles: [`
    .tab-strip {
      margin-bottom: 0;
      display: flex;
      gap: 6px;
      overflow-x: auto;
      overflow-y: hidden;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      padding: 0 0 0 2px;
      position: relative;
      z-index: 2;
    }

    .tab-strip__tab {
      height: 38px;
      border: 1px solid #d1d5db;
      border-bottom-color: #cbd5e1;
      border-radius: 10px 10px 0 0;
      background: #f8fafc;
      color: #374151;
      padding: 0 16px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      flex: 0 0 auto;
      transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
      margin-bottom: 0;
      position: relative;
      z-index: 2;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .tab-strip__tab-label {
      line-height: 1;
    }

    .tab-strip__tab-dot {
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: #f59e0b;
      box-shadow: 0 0 0 1px rgba(217, 119, 6, 0.35);
      flex: 0 0 auto;
    }

    .tab-strip__tab-dot--blinking {
      animation-name: tab-strip-dot-blink;
      animation-timing-function: steps(1, end);
      animation-iteration-count: infinite;
    }

    @keyframes tab-strip-dot-blink {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }

    .tab-strip__tab:hover {
      border-color: #93c5fd;
      background: #eff6ff;
      color: #1e40af;
    }

    .tab-strip--variant-default .tab-strip__tab {
      border-bottom-color: #cbd5e1;
      background: #ffffff;
      box-shadow: inset 0 3px 0 transparent;
      transform: translateY(1px);
    }

    .tab-strip--variant-default .tab-strip__tab--active {
      border-bottom-color: transparent;
    }

    .tab-strip__tab--active {
      border-color: #2563eb;
      border-bottom-color: transparent;
      background: #ffffff;
      color: #1d4ed8;
      box-shadow: inset 0 3px 0 #2563eb;
      border-radius: 10px 10px 0 0;
      transform: translateY(1px);
      z-index: 3;
    }


    .tab-strip__tab--disabled {
      opacity: 0.55;
      cursor: default;
    }

    .tab-strip--stretch .tab-strip__tab {
      flex: 1 1 0;
    }

    .tab-strip--wrap {
      flex-wrap: wrap;
      overflow-x: visible;
      overflow-y: visible;
      white-space: normal;
      row-gap: 6px;
    }

    .tab-strip--size-sm .tab-strip__tab {
      height: 34px;
      padding: 0 10px;
      font-size: 12px;
    }

    .tab-strip--size-md .tab-strip__tab {
      height: 38px;
      padding: 0 16px;
      font-size: 13px;
    }

    .tab-strip--size-lg .tab-strip__tab {
      height: 44px;
      padding: 0 18px;
      font-size: 14px;
    }

    .tab-strip--variant-segmented {
      gap: 8px;
    }

    .tab-strip--variant-segmented .tab-strip__tab {
      border-radius: 10px;
      border-bottom-color: #d1d5db;
      transform: none;
    }

    .tab-strip--variant-segmented .tab-strip__tab--active {
      border-color: #2563eb;
      border-bottom-color: #2563eb;
      background: #eff6ff;
      box-shadow: inset 0 0 0 1px #2563eb;
      transform: none;
    }

    .tab-strip--variant-underline {
      gap: 2px;
      padding-left: 0;
      border-bottom: 1px solid #d1d5db;
    }

    .tab-strip--variant-underline .tab-strip__tab {
      border: none;
      border-radius: 8px 8px 0 0;
      background: transparent;
      color: #4b5563;
    }

    .tab-strip--variant-underline .tab-strip__tab--active {
      border: none;
      border-bottom: 2px solid #2563eb;
      box-shadow: none;
      color: #1d4ed8;
      transform: none;
      background: transparent;
    }

    @media (max-width: 900px) {
      .tab-strip {
        flex-wrap: wrap;
        overflow-x: visible;
        overflow-y: visible;
        white-space: normal;
        row-gap: 6px;
      }

      .tab-strip {
        gap: 4px;
      }

      .tab-strip--size-md .tab-strip__tab,
      .tab-strip--size-lg .tab-strip__tab {
        height: 36px;
        padding: 0 12px;
        font-size: 12px;
      }
    }
  `],
})
export class TabStripComponent implements OnChanges, AfterViewInit {
  @ViewChild('tabStrip')
  private tabStrip?: ElementRef<HTMLElement>;

  @ViewChildren('tabButton')
  private tabButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  @Input()
  public items: TabStripItem[] = [];

  @Input()
  public activeId = '';

  @Input()
  public size: TabStripSize = 'md';

  @Input()
  public variant: TabStripVariant = 'default';

  @Input()
  public stretch = false;

  @Input()
  public wrap = false;

  @Input()
  public ariaLabel = '';

  @Input()
  public autoScrollActive = true;

  @Output()
  public readonly activeIdChange = new EventEmitter<string>();

  @Output()
  public readonly tabClick = new EventEmitter<TabStripClickEvent>();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeId'] || changes['items'] || changes['autoScrollActive']) {
      this.scheduleScrollActiveIntoView();
    }
  }

  public ngAfterViewInit(): void {
    this.scheduleScrollActiveIntoView();
  }

  protected onSelect(item: TabStripItem): void {
    this.tabClick.emit({
      id: item.id,
      item,
      isActive: item.id === this.activeId,
    });

    if (item.id === this.activeId) {
      return;
    }

    this.activeIdChange.emit(item.id);
    this.scheduleScrollActiveIntoView();
  }

  private scheduleScrollActiveIntoView(): void {
    if (!this.autoScrollActive || this.wrap) {
      return;
    }

    setTimeout(() => this.scrollActiveIntoView(), 0);
  }

  private scrollActiveIntoView(): void {
    if (!this.tabButtons?.length || !this.activeId) {
      return;
    }

    const activeIndex = this.items.findIndex((item) => item.id === this.activeId);
    if (activeIndex < 0) {
      return;
    }

    const buttonRef = this.tabButtons.get(activeIndex);
    const buttonElement = buttonRef?.nativeElement;
    if (!buttonElement) {
      return;
    }

    const stripElement = this.tabStrip?.nativeElement;
    if (!stripElement) {
      return;
    }

    const buttonLeft = buttonElement.offsetLeft;
    const buttonRight = buttonLeft + buttonElement.offsetWidth;
    const visibleLeft = stripElement.scrollLeft;
    const visibleRight = visibleLeft + stripElement.clientWidth;

    if (buttonLeft < visibleLeft) {
      stripElement.scrollTo({ left: buttonLeft, behavior: 'smooth' });
      return;
    }

    if (buttonRight > visibleRight) {
      stripElement.scrollTo({
        left: buttonRight - stripElement.clientWidth,
        behavior: 'smooth',
      });
    }
  }
}
